import React, {
  useCallback,
  useRef,
  useEffect,
  useMemo,
  ReactNode,
} from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import intl from 'react-intl-universal';
import { isEqual, orderBy } from 'lodash';
import { Userpilot } from 'userpilot';
import { useAppThunkDispatch } from 'state/store';
import { showNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';
import { addTaskBundle } from 'state/TasksBundle/tasksBundleSlice';
import {
  updateBundle,
  bulkCreateOrUpdateTemplates,
  bulkDeleteTaskTemplatesForBundle,
  bulkCreateTaskTemplateDependencies,
  setTaskTemplates,
  fetchTaskTemplatesForBundle,
  bulkDeleteTaskTemplateDependencies,
} from 'state/ActiveTaskBundle/activeTaskBundleSlice';
import { useTaskBundleDraft } from 'Pages/SettingsPage/Configurations/ProjectSettings/components/TaskBundlePage/providers/TaskBundleProvider/TaskBundleProvider';
import useNavigation from 'Hooks/useNavigation';
import {
  FailedDependencyLink,
  TaskTemplateDependencyLink,
  BulkCreateOrUpdateResult,
} from 'utils/types/taskTemplate';
import { TaskTemplateDraft, TaskTemplateDraftDependencies } from '../types';
import { TaskBundle } from 'utils/customTypes';
import { TaskTemplate } from 'utils/types/taskTemplate';
import { USERPILOT_EVENTS } from 'utils/constants';

interface UseSaveTaskBundleReturn {
  onSave: () => Promise<void>;
}

export function useSaveTaskBundle(): UseSaveTaskBundleReturn {
  const dispatch = useAppThunkDispatch();
  const { goToBackPage } = useNavigation();
  const { newTaskTypes = false, taskDependenciesInBundles = false } =
    useFlags();
  const shouldLinkDependencies = newTaskTypes && taskDependenciesInBundles;
  const { selectors, actions, state } = useTaskBundleDraft();
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const { mode, getTemplateById } = selectors;
  const { draft, initialDraft, modifiedTemplateIds } = state;

  const buildRelativeDatesPayload = useCallback(() => {
    const relativeDateTaskCount = draft.templates.filter(
      (t) =>
        (t.start_day !== '' && t.start_day !== undefined) ||
        (t.duration !== '' && t.duration !== undefined)
    ).length;
    return {
      totalTaskCount: draft.templates.length,
      relativeDateTaskCount,
      hasRelativeDates: relativeDateTaskCount > 0,
    };
  }, [draft.templates]);

  const buildDependenciesPayload = useCallback(() => {
    const tasksWithDependenciesCount = draft.templates.filter(
      (t) =>
        t.dependencies.predecessors.length > 0 ||
        t.dependencies.successors.length > 0
    ).length;
    return {
      tasksWithDependenciesCount,
      hasTaskDependencies: tasksWithDependenciesCount > 0,
    };
  }, [draft.templates]);

  const originalDependenciesMap = useMemo(() => {
    return initialDraft.templates.reduce((acc, template) => {
      acc[template.id] = template.dependencies;
      return acc;
    }, {} as Record<string, TaskTemplateDraftDependencies>);
  }, [initialDraft.templates]);

  const generateFailedDependenciesNotification = useCallback(
    (failedLinks: FailedDependencyLink[]) => {
      // Collect the names of the individual dependency templates that failed,
      // not the source template. Each FailedDependencyLink.failures entry holds
      // the id of the dependency template that could not be linked.
      const failedDependenciesList = failedLinks
        .flatMap((failedLink) => failedLink.failures ?? [])
        .map((failure) => {
          const template = getTemplateById(failure.id);
          return template?.name;
        })
        .filter((name): name is string => Boolean(name));

      // Skip the notification when there are no resolvable names — this avoids
      // showing an empty list (e.g. when the catch branch synthesises a
      // FailedDependencyLink with failures: []).
      if (failedDependenciesList.length === 0) return;

      const message: ReactNode = React.createElement(
        'div',
        { className: 'flex flex-col gap-2' },
        React.createElement(
          'p',
          null,
          intl.get(
            'SETTINGS_PAGE.TASK_BUNDLE_PAGE.DEPENDENCIES.CREATE_DEPENDENCIES_ERROR'
          )
        ),
        React.createElement(
          'ul',
          { className: 'list-disc pl-4' },
          failedDependenciesList.map((name, index) =>
            React.createElement('li', { key: index }, name)
          )
        )
      );

      dispatch(
        showNotification({
          autoHide: true,
          notificationVariant: 'error',
          notificationTitle: intl.get('SNACKBAR_ERROR_NOTIFICATION_TITLE'),
          notificationMessage: message,
          timeout: 8000,
        })
      );
    },
    [dispatch, getTemplateById]
  );

  const generateFailedRemovedDependenciesNotification = useCallback(
    (failedDeps: string[]) => {
      const failedDependenciesList = [
        ...new Set(
          failedDeps
            .map((failedDep) => {
              const template = getTemplateById(failedDep);
              return template?.name;
            })
            .filter((name): name is string => Boolean(name))
        ),
      ];

      if (failedDependenciesList.length === 0) return;

      const message: ReactNode = React.createElement(
        'div',
        { className: 'flex flex-col gap-2' },
        React.createElement(
          'p',
          null,
          intl.get(
            'SETTINGS_PAGE.TASK_BUNDLE_PAGE.DEPENDENCIES.DELETE_DEPENDENCIES_ERROR'
          )
        ),
        React.createElement(
          'ul',
          { className: 'list-disc pl-4' },
          failedDependenciesList.map((name) =>
            React.createElement('li', { key: name }, name)
          )
        )
      );

      dispatch(
        showNotification({
          autoHide: true,
          notificationVariant: 'error',
          notificationTitle: intl.get('SNACKBAR_ERROR_NOTIFICATION_TITLE'),
          notificationMessage: message,
          timeout: 8000,
        })
      );
    },
    [dispatch, getTemplateById]
  );

  const buildDependencyLinks = useCallback(
    (
      idMap: Map<string, string>,
      templatesToDelete: Set<string>
    ): {
      linksToSave: TaskTemplateDependencyLink[];
      linksToRemove: TaskTemplateDependencyLink[];
    } => {
      const linksToSave: TaskTemplateDependencyLink[] = [];
      const linksToRemove: TaskTemplateDependencyLink[] = [];
      const seenEdges = new Set<string>();

      const getEdgeKey = (from: string, to: string): string => {
        return `${from}|${to}`;
      };

      for (const template of draft.templates) {
        const templateServerId = idMap.get(template.id);
        if (!templateServerId) continue;

        for (const direction of ['predecessors', 'successors'] as const) {
          const clientDependencyIds = template.dependencies[direction];

          let dependenciesToSave: string[] = [];
          let dependenciesToRemove: string[] = [];
          let filteredDependenciesToRemove: string[] = [];

          if (mode === 'edit') {
            const originalDependencies =
              originalDependenciesMap[template.id]?.[direction] || [];
            dependenciesToSave = clientDependencyIds.filter(
              (clientId) => !originalDependencies.includes(clientId)
            );
            dependenciesToRemove = originalDependencies.filter(
              (originalId) => !clientDependencyIds.includes(originalId)
            );

            // Filter out dependencies pointing to deleted templates
            // (backend trigger will handle them automatically)
            filteredDependenciesToRemove = dependenciesToRemove.filter(
              (clientId) => !templatesToDelete.has(clientId)
            );
          } else {
            dependenciesToSave = clientDependencyIds;
          }

          if (dependenciesToSave.length > 0) {
            const depsToAdd = dependenciesToSave
              .map((clientId) => idMap.get(clientId))
              .filter((serverId): serverId is string => Boolean(serverId));

            const filteredDepsToAdd = depsToAdd.filter((depServerId) => {
              const edgeKey =
                direction === 'successors'
                  ? getEdgeKey(templateServerId, depServerId)
                  : getEdgeKey(depServerId, templateServerId);

              if (seenEdges.has(edgeKey)) {
                return false;
              }
              seenEdges.add(edgeKey);
              return true;
            });

            if (filteredDepsToAdd.length > 0) {
              linksToSave.push({
                taskTemplateId: templateServerId,
                direction,
                dependencies: filteredDepsToAdd,
              });
            }
          }
          if (filteredDependenciesToRemove.length > 0) {
            const depsToRemove = filteredDependenciesToRemove
              .map((clientId) => idMap.get(clientId))
              .filter((serverId): serverId is string => Boolean(serverId));

            const deduplicatedDepsToRemove = depsToRemove.filter(
              (depServerId) => {
                const edgeKey =
                  direction === 'successors'
                    ? getEdgeKey(templateServerId, depServerId)
                    : getEdgeKey(depServerId, templateServerId);

                if (seenEdges.has(edgeKey)) {
                  return false;
                }
                seenEdges.add(edgeKey);
                return true;
              }
            );

            if (deduplicatedDepsToRemove.length > 0) {
              linksToRemove.push({
                taskTemplateId: templateServerId,
                direction,
                dependencies: deduplicatedDepsToRemove,
              });
            }
          }
        }
      }

      return { linksToSave, linksToRemove };
    },
    [draft.templates, originalDependenciesMap, mode]
  );

  const linkDependencies = useCallback(
    async (
      bundleId: string,
      idMap: Map<string, string>,
      templatesToDelete: Set<string>
    ) => {
      if (!shouldLinkDependencies) return;

      const { linksToSave, linksToRemove } = buildDependencyLinks(
        idMap,
        templatesToDelete
      );

      if (linksToRemove.length > 0) {
        const { failedDeps } = await dispatch(
          bulkDeleteTaskTemplateDependencies({
            bundleId,
            links: linksToRemove,
          })
        ).unwrap();
        if (failedDeps.length > 0) {
          generateFailedRemovedDependenciesNotification(failedDeps);
        }
      }

      if (linksToSave.length > 0) {
        const { failedLinks } = await dispatch(
          bulkCreateTaskTemplateDependencies({ bundleId, links: linksToSave })
        ).unwrap();
        if (failedLinks.length > 0) {
          generateFailedDependenciesNotification(failedLinks);
        }
      }
    },
    [
      shouldLinkDependencies,
      buildDependencyLinks,
      dispatch,
      generateFailedDependenciesNotification,
      generateFailedRemovedDependenciesNotification,
    ]
  );

  const updateTaskTemplatesReduxState = useCallback(
    (idMap: Map<string, string>) => {
      const templates = draft.templates.map<TaskTemplate>(
        (template: TaskTemplateDraft, index: number) => ({
          id: idMap.get(template.id) ?? '',
          name: template.name,
          description: template.description,
          estimated_hours: template.estimated_hours,
          type: template.type,
          start_day: template.start_day,
          duration: template.duration,
          index,
        })
      );
      const sortedTemplates = orderBy(templates, ['index'], ['asc']);
      dispatch(setTaskTemplates(sortedTemplates));
    },
    [draft.templates, dispatch]
  );

  /**
   * Remap client UUIDs in a draft to server IDs using idMap so that
   * `markSaved` stores a draft that matches the redux state (server IDs).
   * Without this remapping, the Provider's effect detects a mismatch and
   * resets the draft on the next render.
   */
  const remapDraftIds = useCallback(
    (currentDraft: typeof draft, idMap: Map<string, string>) => {
      return {
        ...currentDraft,
        templates: currentDraft.templates.map((template) => {
          const serverId = idMap.get(template.id) ?? template.id;
          return {
            ...template,
            id: serverId,
            dependencies: {
              predecessors: template.dependencies.predecessors.map(
                (id) => idMap.get(id) ?? id
              ),
              successors: template.dependencies.successors.map(
                (id) => idMap.get(id) ?? id
              ),
            },
          };
        }),
      };
    },
    []
  );

  const saveCreate = useCallback(async () => {
    let createdBundle: TaskBundle | undefined;
    try {
      const { taskBundle } = await dispatch(
        addTaskBundle({
          bundleData: {
            name: draft.bundle.name,
            description: draft.bundle.description,
          },
          useNewApi: newTaskTypes,
        })
      ).unwrap();
      createdBundle = taskBundle;
    } catch (error) {
      dispatch(
        showNotification({
          autoHide: true,
          notificationVariant: 'error',
          notificationTitle: intl.get('SNACKBAR_ERROR_NOTIFICATION_TITLE'),
          notificationMessage: intl.get(
            'SETTINGS_PAGE.TASK_BUNDLE_PAGE.CREATE_BUNDLE_ERROR'
          ),
        })
      );
      throw error;
    }

    if (draft.templates.length > 0 && createdBundle && createdBundle.id) {
      const templatesToCreate = draft.templates.map((template, index) => ({
        id: template.id,
        name: template.name,
        description: template.description,
        estimated_hours: template.estimated_hours,
        type: template.type,
        start_day: template.start_day !== '' ? template.start_day : undefined,
        duration: template.duration !== '' ? template.duration : undefined,
        action_type: 'create' as const,
        index,
      }));
      let bulkResults: BulkCreateOrUpdateResult[] | undefined;
      try {
        bulkResults = await dispatch(
          bulkCreateOrUpdateTemplates({
            templates: templatesToCreate,
            bundleId: createdBundle.id,
            useNewApi: newTaskTypes,
          })
        ).unwrap();
      } catch (error) {
        dispatch(
          showNotification({
            autoHide: true,
            notificationVariant: 'error',
            notificationTitle: intl.get('SNACKBAR_ERROR_NOTIFICATION_TITLE'),
            notificationMessage: intl.get(
              'SETTINGS_PAGE.TASK_BUNDLE_PAGE.BUILD_SECTION.CREATE_TASKS_ERROR'
            ),
          })
        );
        throw error;
      }

      if (bulkResults) {
        const idMap = new Map<string, string>(
          bulkResults.map((result) => [result.clientId, result.serverId])
        );
        updateTaskTemplatesReduxState(idMap);
        await linkDependencies(createdBundle.id, idMap, new Set());
        // Remap draft before markSaved so the stored initialDraft uses server
        // IDs and the Provider's equality check does not trigger reset_draft.
        const remappedDraft = remapDraftIds(draft, idMap);
        if (!isMountedRef.current) return;
        dispatch(
          showNotification({
            autoHide: true,
            notificationVariant: 'success',
            notificationTitle: intl.get(
              'SETTINGS_PAGE.TASK_BUNDLE_PAGE.CREATED_SUCCESSFULLY'
            ),
          })
        );
        try {
          Userpilot.track(USERPILOT_EVENTS.BUNDLE_CREATED, {
            ...buildRelativeDatesPayload(),
            ...buildDependenciesPayload(),
          });
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(
            'Error tracking Userpilot event for bundle created',
            error
          );
        }
        actions.markSaved(remappedDraft);
        goToBackPage();
        return;
      }
    }

    if (!isMountedRef.current) return;

    dispatch(
      showNotification({
        autoHide: true,
        notificationVariant: 'success',
        notificationTitle: intl.get(
          'SETTINGS_PAGE.TASK_BUNDLE_PAGE.CREATED_SUCCESSFULLY'
        ),
      })
    );

    try {
      Userpilot.track(USERPILOT_EVENTS.BUNDLE_CREATED, {
        ...buildRelativeDatesPayload(),
        ...buildDependenciesPayload(),
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error tracking Userpilot event for bundle created', error);
    }
    actions.markSaved(draft);
    goToBackPage();
  }, [
    dispatch,
    actions,
    goToBackPage,
    draft,
    newTaskTypes,
    linkDependencies,
    updateTaskTemplatesReduxState,
    remapDraftIds,
    isMountedRef,
    buildRelativeDatesPayload,
    buildDependenciesPayload,
  ]);

  const saveEdit = useCallback(async () => {
    const bundleId = draft.bundle.id;
    if (!bundleId) return;

    // Update bundle overview if changed
    const overviewChanged = !isEqual(draft.bundle, initialDraft.bundle);
    if (overviewChanged) {
      await dispatch(
        updateBundle({
          bundle: {
            id: bundleId,
            name: draft.bundle.name,
            description: draft.bundle.description,
          } as TaskBundle,
          useNewApi: newTaskTypes,
        })
      );
    }

    // Compute templates to delete
    const originalIds = new Set(initialDraft.templates.map((t) => t.id));
    const currentIds = new Set(draft.templates.map((t) => t.id));
    const templatesToDelete = new Set(
      [...originalIds].filter((id) => !currentIds.has(id))
    );

    // The bulk-reorder endpoint (CP-10792, Cognota/tasks#285) requires the
    // payload's update-id set to match the bundle's active-template-id set
    // exactly. Iterating only modifiedTemplateIds would send a partial list
    // and the server's strict validation rejects it with 400 — and even with
    // lenient validation any unsent template ends up at NULL position. So
    // when we make the call, we send every active template in the new local
    // order, marking each item 'create' (no original id) or 'update'. Gate
    // on modifiedTemplateIds / templatesToDelete so an untouched bundle
    // doesn't trigger a no-op round-trip.
    const hasTemplateChanges =
      modifiedTemplateIds.size > 0 || templatesToDelete.size > 0;
    const templatesToCreateOrUpdate = hasTemplateChanges
      ? draft.templates.map((template, index) => {
          const isNew = !originalIds.has(template.id);
          return {
            id: template.id,
            name: template.name,
            description: template.description,
            estimated_hours: template.estimated_hours,
            type: template.type,
            start_day:
              template.start_day !== '' ? template.start_day : undefined,
            duration: template.duration !== '' ? template.duration : undefined,
            action_type: isNew ? ('create' as const) : ('update' as const),
            index,
          };
        })
      : [];

    // Delete templates if needed
    let creatorIsNotified = false;
    const idMap = new Map<string, string>(
      initialDraft.templates.map((t) => [t.id, t.id])
    );

    if (templatesToDelete.size > 0) {
      try {
        await dispatch(
          bulkDeleteTaskTemplatesForBundle({
            bundleId,
            templatesToDelete: [...templatesToDelete],
            useNewApi: newTaskTypes,
          })
        ).unwrap();
      } catch (error) {
        dispatch(
          showNotification({
            autoHide: true,
            notificationVariant: 'error',
            notificationTitle: intl.get('SNACKBAR_ERROR_NOTIFICATION_TITLE'),
            notificationMessage: intl.get(
              'SETTINGS_PAGE.TASK_BUNDLE_PAGE.BUILD_SECTION.DELETE_TASKS_ERROR'
            ),
          })
        );
        throw error;
      }
      creatorIsNotified = true;
    }

    // Create or update templates if needed
    if (templatesToCreateOrUpdate.length > 0) {
      let bulkResults: BulkCreateOrUpdateResult[] | undefined;
      try {
        bulkResults = await dispatch(
          bulkCreateOrUpdateTemplates({
            templates: templatesToCreateOrUpdate,
            bundleId,
            creatorIsNotified,
            useNewApi: newTaskTypes,
          })
        ).unwrap();
      } catch (error) {
        dispatch(
          showNotification({
            autoHide: true,
            notificationVariant: 'error',
            notificationTitle: intl.get('SNACKBAR_ERROR_NOTIFICATION_TITLE'),
            notificationMessage: intl.get(
              'SETTINGS_PAGE.TASK_BUNDLE_PAGE.BUILD_SECTION.CREATE_TASKS_ERROR'
            ),
          })
        );
        throw error;
      }

      if (bulkResults) {
        for (const result of bulkResults) {
          idMap.set(result.clientId, result.serverId);
        }
      }
    }

    // Update task templates redux state if needed
    if (newTaskTypes) {
      updateTaskTemplatesReduxState(idMap);
    } else {
      dispatch(
        fetchTaskTemplatesForBundle({ bundleId, useNewApi: newTaskTypes })
      );
    }
    // Link dependencies if needed
    await linkDependencies(bundleId, idMap, templatesToDelete);

    if (!isMountedRef.current) return;

    dispatch(
      showNotification({
        autoHide: true,
        notificationVariant: 'success',
        notificationTitle: intl.get(
          'SETTINGS_PAGE.TASK_BUNDLE_PAGE.UPDATED_SUCCESSFULLY'
        ),
      })
    );

    // Remap draft IDs through idMap before calling markSaved so that the
    // stored initialDraft uses server IDs and won't mismatch redux state on
    // the next render (which would trigger an unwanted reset_draft).
    const remappedDraft = remapDraftIds(draft, idMap);

    try {
      Userpilot.track(USERPILOT_EVENTS.BUNDLE_UPDATED, {
        ...buildRelativeDatesPayload(),
        ...buildDependenciesPayload(),
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error tracking Userpilot event for bundle updated', error);
    }

    // Mark as saved (resets dirty state)
    actions.markSaved(remappedDraft);
  }, [
    draft,
    initialDraft,
    modifiedTemplateIds,
    newTaskTypes,
    dispatch,
    linkDependencies,
    actions,
    updateTaskTemplatesReduxState,
    remapDraftIds,
    isMountedRef,
    buildRelativeDatesPayload,
    buildDependenciesPayload,
  ]);

  const onSave = useCallback(async () => {
    try {
      if (mode === 'edit') {
        await saveEdit();
      } else {
        await saveCreate();
      }
    } catch {
      // Errors thrown by saveCreate/saveEdit are already surfaced as snackbar
      // notifications inside those functions. Swallowing here prevents an
      // unhandled-promise-rejection from propagating to the browser console
      // while still allowing individual operations to short-circuit cleanly.
    }
  }, [mode, saveCreate, saveEdit]);

  return { onSave };
}
